import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, Building2, Image as ImageIcon, Home, Star, LandPlot,
  Images, MapPinned, Tag, HelpCircle, MessageSquareQuote, LogOut, Save, Loader2, Trash2, Globe,
} from "lucide-react";
import { getMe, getContent, getLeads, deleteLead, saveContent, formatApiError } from "@/lib/api";
import { Field, ImageField, ListEditor } from "./admin/editors";

const TABS = [
  { id: "leads", label: "Leads", icon: Users },
  { id: "umum", label: "Umum & Kontak", icon: Building2 },
  { id: "hero", label: "Hero", icon: LayoutDashboard },
  { id: "proyek", label: "Proyek", icon: Home },
  { id: "type36", label: "Type 36", icon: Star },
  { id: "keunggulan", label: "Keunggulan", icon: Star },
  { id: "fasilitas", label: "Fasilitas", icon: LandPlot },
  { id: "galeri", label: "Galeri", icon: Images },
  { id: "siteplan", label: "Site Plan & Lokasi", icon: MapPinned },
  { id: "harga", label: "Harga & Promo", icon: Tag },
  { id: "faq", label: "FAQ", icon: HelpCircle },
  { id: "testimoni", label: "Testimoni", icon: MessageSquareQuote },
];

function setDeep(obj, path, value) {
  const clone = JSON.parse(JSON.stringify(obj));
  let cur = clone;
  for (let i = 0; i < path.length - 1; i++) cur = cur[path[i]];
  cur[path[path.length - 1]] = value;
  return clone;
}

const getDeep = (obj, path) => path.reduce((o, k) => (o ? o[k] : ""), obj);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [content, setContent] = useState(null);
  const [leads, setLeads] = useState([]);
  const [tab, setTab] = useState("leads");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [user, c, l] = await Promise.all([getMe(), getContent(), getLeads()]);
      setMe(user);
      setContent(c);
      setLeads(l);
    } catch (err) {
      localStorage.removeItem("csm_token");
      navigate("/admin/login");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    document.title = "Admin Dashboard — PT. Cahaya Sakofa Mulia";
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (path) => (value) => setContent((c) => setDeep(c, path, value));

  const save = async () => {
    setSaving(true);
    try {
      await saveContent(content);
      toast.success("Konten berhasil disimpan");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const removeLead = async (id) => {
    try {
      await deleteLead(id);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Lead dihapus");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const logout = () => {
    localStorage.removeItem("csm_token");
    navigate("/admin/login");
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-brand-mist" data-testid="admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  const SaveBar = () => (
    <div className="mt-8 flex justify-end">
      <button
        onClick={save}
        disabled={saving}
        data-testid="admin-save-button"
        className="inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-deep active:scale-95 disabled:opacity-60"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        Simpan Perubahan
      </button>
    </div>
  );

  const renderTab = () => {
    switch (tab) {
      case "leads":
        return (
          <div data-testid="admin-leads-tab">
            <h2 className="font-display text-xl font-bold text-brand-dark">Leads Calon Pembeli ({leads.length})</h2>
            <p className="mt-1 text-sm text-slate-500">Data pengunjung yang mengisi formulir minat pada website.</p>
            <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-brand-mist text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3">Tanggal</th>
                    <th className="px-4 py-3">Nama</th>
                    <th className="px-4 py-3">WhatsApp</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Tipe</th>
                    <th className="px-4 py-3">Budget</th>
                    <th className="px-4 py-3">Pesan</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {leads.length === 0 && (
                    <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400" data-testid="leads-empty">Belum ada leads masuk.</td></tr>
                  )}
                  {leads.map((l, i) => (
                    <tr key={l.id} className="border-b border-slate-100 last:border-0 hover:bg-brand-mist/50" data-testid={`lead-row-${i}`}>
                      <td className="px-4 py-3 text-slate-500">{l.createdAt ? new Date(l.createdAt).toLocaleString("id-ID") : "-"}</td>
                      <td className="px-4 py-3 font-semibold text-brand-dark">{l.name}</td>
                      <td className="px-4 py-3">{l.whatsapp}</td>
                      <td className="px-4 py-3">{l.email || "-"}</td>
                      <td className="px-4 py-3">{l.houseType || "-"}</td>
                      <td className="px-4 py-3">{l.budget || "-"}</td>
                      <td className="max-w-[220px] truncate px-4 py-3" title={l.message}>{l.message || "-"}</td>
                      <td className="px-4 py-3">
                        <button onClick={() => removeLead(l.id)} aria-label="Hapus lead" data-testid={`lead-delete-${i}`} className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      case "umum":
        return (
          <div className="grid gap-5 sm:grid-cols-2" data-testid="admin-umum-tab">
            <Field label="Nama Perusahaan" value={getDeep(content, ["site", "name"])} onChange={set(["site", "name"])} testId="f-site-name" />
            <Field label="Tagline" value={getDeep(content, ["site", "tagline"])} onChange={set(["site", "tagline"])} testId="f-site-tagline" />
            <Field label="Nomor WhatsApp Marketing" value={content.whatsapp} onChange={set(["whatsapp"])} hint="Format: 08xxxxxxxxxx — otomatis dikonversi ke tautan wa.me" testId="f-whatsapp" />
            <Field label="Pesan Otomatis WhatsApp" value={content.whatsappMessage} onChange={set(["whatsappMessage"])} textarea testId="f-wa-message" />
            <Field label="Judul Tentang Perusahaan" value={getDeep(content, ["company", "title"])} onChange={set(["company", "title"])} testId="f-company-title" />
            <Field label="Tahun Berdiri" value={getDeep(content, ["company", "foundedYear"])} onChange={set(["company", "foundedYear"])} testId="f-company-year" />
            <div className="sm:col-span-2"><Field label="Deskripsi Perusahaan" value={getDeep(content, ["company", "description"])} onChange={set(["company", "description"])} textarea testId="f-company-desc" /></div>
            <Field label="Alamat Kantor" value={getDeep(content, ["company", "address"])} onChange={set(["company", "address"])} testId="f-company-address" />
            <div className="sm:col-span-2"><ImageField label="Foto Tentang Perusahaan" value={getDeep(content, ["company", "image"])} onChange={set(["company", "image"])} testId="f-company-image" /></div>
            <Field label="Telepon" value={getDeep(content, ["contact", "phone"])} onChange={set(["contact", "phone"])} testId="f-contact-phone" />
            <Field label="Email" value={getDeep(content, ["contact", "email"])} onChange={set(["contact", "email"])} testId="f-contact-email" />
            <Field label="Alamat (Footer/Kontak)" value={getDeep(content, ["contact", "address"])} onChange={set(["contact", "address"])} testId="f-contact-address" />
            <Field label="Instagram" value={getDeep(content, ["contact", "instagram"])} onChange={set(["contact", "instagram"])} testId="f-contact-instagram" />
            <Field label="Facebook" value={getDeep(content, ["contact", "facebook"])} onChange={set(["contact", "facebook"])} testId="f-contact-facebook" />
            <Field label="TikTok" value={getDeep(content, ["contact", "tiktok"])} onChange={set(["contact", "tiktok"])} testId="f-contact-tiktok" />
            <div className="sm:col-span-2"><SaveBar /></div>
          </div>
        );
      case "hero":
        return (
          <div className="grid gap-5" data-testid="admin-hero-tab">
            <Field label="Badge" value={getDeep(content, ["hero", "badge"])} onChange={set(["hero", "badge"])} testId="f-hero-badge" />
            <Field label="Headline" value={getDeep(content, ["hero", "headline"])} onChange={set(["hero", "headline"])} testId="f-hero-headline" />
            <Field label="Subheadline" value={getDeep(content, ["hero", "subheadline"])} onChange={set(["hero", "subheadline"])} textarea testId="f-hero-subheadline" />
            <ImageField label="Gambar Hero" value={getDeep(content, ["hero", "image"])} onChange={set(["hero", "image"])} testId="f-hero-image" />
            <SaveBar />
          </div>
        );
      case "proyek":
        return (
          <div data-testid="admin-proyek-tab">
            <ListEditor
              items={content.projects}
              onChange={set(["projects"])}
              addLabel="Tambah Proyek"
              itemTitle="name"
              testIdPrefix="proj"
              fields={[
                { key: "name", label: "Nama Cluster" },
                { key: "location", label: "Lokasi" },
                { key: "type", label: "Tipe Rumah" },
                { key: "status", label: "Status Unit" },
                { key: "landArea", label: "Luas Tanah" },
                { key: "buildingArea", label: "Luas Bangunan" },
                { key: "bedrooms", label: "Kamar Tidur" },
                { key: "bathrooms", label: "Kamar Mandi" },
                { key: "price", label: "Harga Mulai" },
                { key: "units", label: "Jumlah Unit" },
                { key: "image", label: "Foto Proyek", image: true },
              ]}
            />
            <SaveBar />
          </div>
        );
      case "type36":
        return (
          <div data-testid="admin-type36-tab">
            <ListEditor
              items={content.typeHouse?.units || []}
              onChange={set(["typeHouse", "units"])}
              addLabel="Tambah Unit"
              itemTitle="name"
              testIdPrefix="unit"
              fields={[
                { key: "name", label: "Nama / Tipe Unit" },
                { key: "location", label: "Lokasi" },
                { key: "price", label: "Harga" },
                { key: "dp", label: "Uang Muka / DP (kosongkan jika tidak ada)" },
                { key: "landArea", label: "Luas Tanah" },
                { key: "buildingArea", label: "Luas Bangunan" },
                { key: "bedrooms", label: "Kamar Tidur" },
                { key: "bathrooms", label: "Kamar Mandi" },
                { key: "dpNote", label: "Catatan DP", textarea: true },
                { key: "features", label: "Fasilitas (satu per baris)", lines: true },
                { key: "image", label: "Foto Unit", image: true },
              ]}
            />
            <SaveBar />
          </div>
        );
      case "keunggulan":
        return (
          <div data-testid="admin-keunggulan-tab">
            <ListEditor
              items={content.advantages}
              onChange={set(["advantages"])}
              addLabel="Tambah Keunggulan"
              itemTitle="title"
              testIdPrefix="adv"
              fields={[
                { key: "icon", label: "Ikon", icon: true },
                { key: "title", label: "Judul" },
                { key: "description", label: "Deskripsi", textarea: true },
              ]}
            />
            <SaveBar />
          </div>
        );
      case "fasilitas":
        return (
          <div data-testid="admin-fasilitas-tab">
            <ListEditor
              items={content.facilities}
              onChange={set(["facilities"])}
              addLabel="Tambah Fasilitas"
              itemTitle="name"
              testIdPrefix="fac"
              fields={[
                { key: "icon", label: "Ikon", icon: true },
                { key: "name", label: "Nama Fasilitas" },
                { key: "description", label: "Deskripsi", textarea: true },
              ]}
            />
            <SaveBar />
          </div>
        );
      case "galeri":
        return (
          <div data-testid="admin-galeri-tab">
            <ListEditor
              items={content.gallery}
              onChange={set(["gallery"])}
              addLabel="Tambah Foto"
              itemTitle="caption"
              testIdPrefix="gal"
              fields={[
                { key: "url", label: "URL Foto", image: true },
                { key: "caption", label: "Keterangan" },
                { key: "category", label: "Kategori" },
              ]}
            />
            <SaveBar />
          </div>
        );
      case "siteplan":
        return (
          <div data-testid="admin-siteplan-tab">
            <ListEditor
              items={content.locations || []}
              onChange={set(["locations"])}
              addLabel="Tambah Lokasi Proyek"
              itemTitle="name"
              testIdPrefix="loc"
              fields={[
                { key: "name", label: "Nama Proyek" },
                { key: "address", label: "Alamat" },
                { key: "mapsEmbed", label: "Google Maps Embed URL", textarea: true },
                { key: "sitePlan", label: "Gambar Site Plan", image: true },
                { key: "accessInfo", label: "Informasi Akses", textarea: true },
                { key: "landmarks", label: "Landmark Terdekat", textarea: true },
              ]}
            />
            <SaveBar />
          </div>
        );
      case "harga":
        return (
          <div className="grid gap-5 sm:grid-cols-2" data-testid="admin-harga-tab">
            <Field label="Harga Mulai" value={getDeep(content, ["pricing", "priceStart"])} onChange={set(["pricing", "priceStart"])} testId="f-price" />
            <Field label="DP Mulai" value={getDeep(content, ["pricing", "dpStart"])} onChange={set(["pricing", "dpStart"])} testId="f-dp" />
            <Field label="Cicilan Mulai" value={getDeep(content, ["pricing", "installmentStart"])} onChange={set(["pricing", "installmentStart"])} testId="f-installment" />
            <Field label="Ketersediaan Unit" value={getDeep(content, ["pricing", "availability"])} onChange={set(["pricing", "availability"])} testId="f-availability" />
            <div className="sm:col-span-2"><Field label="Promo Saat Ini" value={getDeep(content, ["pricing", "promo"])} onChange={set(["pricing", "promo"])} textarea testId="f-promo" /></div>
            <div className="sm:col-span-2"><SaveBar /></div>
          </div>
        );
      case "faq":
        return (
          <div data-testid="admin-faq-tab">
            <ListEditor
              items={content.faqs}
              onChange={set(["faqs"])}
              addLabel="Tambah FAQ"
              itemTitle="question"
              testIdPrefix="faq"
              fields={[
                { key: "question", label: "Pertanyaan" },
                { key: "answer", label: "Jawaban", textarea: true },
              ]}
            />
            <SaveBar />
          </div>
        );
      case "testimoni":
        return (
          <div data-testid="admin-testimoni-tab">
            <ListEditor
              items={content.testimonials}
              onChange={set(["testimonials"])}
              addLabel="Tambah Testimoni"
              itemTitle="name"
              testIdPrefix="tst"
              fields={[
                { key: "name", label: "Nama" },
                { key: "role", label: "Peran / Status" },
                { key: "text", label: "Isi Testimoni", textarea: true },
              ]}
            />
            <SaveBar />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex min-h-screen bg-brand-mist" data-testid="admin-dashboard">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-slate-200 bg-white lg:flex" data-testid="admin-sidebar">
        <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-5">
          <img src="/assets/logo.jpeg" alt="Logo" className="h-10 w-10 rounded-lg object-cover" />
          <div className="leading-tight">
            <p className="font-display text-sm font-bold text-brand-dark">Admin CSM</p>
            <p className="text-[10px] uppercase tracking-[0.15em] text-slate-400">{me?.email}</p>
          </div>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
                tab === t.id ? "bg-brand text-white" : "text-slate-600 hover:bg-brand-mist hover:text-brand"
              }`}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
              {t.id === "leads" && leads.length > 0 && (
                <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold ${tab === "leads" ? "bg-white text-brand" : "bg-brand text-white"}`}>
                  {leads.length}
                </span>
              )}
            </button>
          ))}
        </nav>
        <div className="space-y-1 border-t border-slate-100 p-3">
          <a href="/" target="_blank" rel="noopener" className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-brand-mist hover:text-brand" data-testid="admin-view-site">
            <Globe className="h-4 w-4" /> Lihat Website
          </a>
          <button onClick={logout} data-testid="admin-logout" className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-red-50 hover:text-red-600">
            <LogOut className="h-4 w-4" /> Keluar
          </button>
        </div>
      </aside>

      <div className="flex-1 lg:ml-64">
        <div className="sticky top-0 z-30 flex items-center gap-2 overflow-x-auto border-b border-slate-200 bg-white/90 px-4 py-3 backdrop-blur lg:hidden" data-testid="admin-mobile-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-xs font-semibold transition-colors ${tab === t.id ? "bg-brand text-white" : "bg-brand-mist text-slate-600"}`}
            >
              {t.label}
            </button>
          ))}
          <button onClick={logout} className="whitespace-nowrap rounded-full bg-red-50 px-4 py-2 text-xs font-semibold text-red-600" data-testid="admin-logout-mobile">Keluar</button>
        </div>
        <main className="mx-auto max-w-5xl p-4 sm:p-8">{renderTab()}</main>
      </div>
    </div>
  );
}
